import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, Lock, Plus, Search, Sparkles, Trash2, X } from 'lucide-react';
import {
    DolderAlternativePicker,
    type AlternativeEntry,
    type AlternativeSearchSource,
} from './DolderAlternativePicker';
import { visitReportsApi } from '../../services/visitReportsApi';
import type {
    MarketObservation,
    MarketObservationAlternative,
    MarketObservationEnumOption,
    MarketObservationEnumResolution,
    MarketObservationOptionsResponse,
} from '../../types/visitReport';

/**
 * *Market data* in the visit report: what the customer buys elsewhere.
 *
 * Phase 4 **captures**. Nothing here writes to HubSpot — phase 5 turns each observation into a
 * `marketdata` record in a separate, acknowledged step after the report has synced.
 *
 * ── THE THREE RULES OF THIS SECTION ────────────────────────────────────────────────────────
 *
 * 1. **A suggestion is not a value.** `vendor`, `item_category` and `currency_code` are HubSpot
 *    enumerations with 879, 391 and 6 allowed values, and the interview runs in seven
 *    languages. What the AI heard is shown as a suggestion with the server's verdict beside it;
 *    the value that a write will use can only come from the list the server read out of the
 *    LIVE schema. An unmatched suggestion stays visible and unapplied rather than being
 *    silently dropped — the rep is the one who knows whether "BASF" meant BASF SE.
 *
 * 2. **Without an ERP customer the section is locked, and it says why.** `dolder_company` is
 *    create-only on the marketdata object and is derived from the ERP company code. A report on
 *    a HubSpot-only company (which `interview.ts` explicitly allows) has none, and `dat`/`dde`
 *    have no `HubspotCode` at all. Locking with the reason beats collecting data phase 5 would
 *    have to refuse.
 *
 * 3. **The alternatives picker is the one from P1**, not a second copy of it — same component,
 *    same `displayText` rules, same `|` separator, same primary-is-position-0 meaning. It
 *    searches through this page's own routes so the permission guard stays per page.
 */

const VISIT_REPORT_SEARCH: AlternativeSearchSource = {
    products: async (companyCode, q) => (await visitReportsApi.searchAlternativeProducts(companyCode, q)).items,
    categories: async (companyCode, q) => (await visitReportsApi.searchAlternativeCategories(companyCode, q)).items,
};

interface Props {
    observations: MarketObservation[];
    onChange: (next: MarketObservation[]) => void;
    /** The ERP company of the customer. Undefined = HubSpot-only company, section locked. */
    companyCode?: string;
    disabled?: boolean;
}

/**
 * Said before the server gets a chance to say it. The server's wording is authoritative and
 * replaces this as soon as the call returns; this exists so the rep is not shown an open form
 * for a second on a report that can never carry market data.
 */
const NO_ERP_CUSTOMER_REASON =
    'This report is on a HubSpot company without an ERP customer. Market data records carry a '
    + 'Dolder entity that is derived from the ERP company code and can never be changed afterwards, '
    + 'so there is nothing to file them under. Pick the ERP customer in step 1 to capture market data.';

/** Stable React keys for rows the rep reorders and deletes. Never sent anywhere. */
let rowSeq = 0;
const nextRowKey = () => `obs-${++rowSeq}`;

export function MarketObservations({ observations, onChange, companyCode, disabled = false }: Props) {
    const [options, setOptions] = useState<MarketObservationOptionsResponse | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [resolutions, setResolutions] = useState<
        Array<Record<'vendor' | 'itemCategory' | 'currency', MarketObservationEnumResolution>>
    >([]);
    /** Row keys, held outside the draft so a saved report carries no UI state. */
    const keysRef = useRef<string[]>([]);
    /** The server's verdict is applied to the draft exactly once, on the first load. */
    const appliedRef = useRef(false);
    /**
     * Whether the value lists have been asked for.
     *
     * They are the only HubSpot-backed request in this section, and most visit reports carry no
     * market data at all. So the section renders with the gate alone — a database lookup — and
     * asks for the 879 vendors, 391 categories and 6 currencies the moment it has a row, or the
     * rep adds one.
     */
    const [wantsOptions, setWantsOptions] = useState(observations.length > 0);

    while (keysRef.current.length < observations.length) keysRef.current.push(nextRowKey());

    // The gate when the section appears; the value lists only once they are needed. The
    // interview, the autosave and a reload cost no HubSpot call at all.
    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setLoadError(null);
        visitReportsApi.marketObservationOptions(companyCode, observations, wantsOptions)
            .then(res => {
                if (cancelled) return;
                setOptions(res);
                setResolutions(res.resolved);

                // Adopt what the server matched with certainty, once. An unmatched suggestion
                // is left for the rep: `resolveEnumSuggestion` never guesses past a unique
                // prefix, because a plausible wrong vendor on a live CRM record is worse than
                // an empty field somebody has to fill.
                if (appliedRef.current || res.blockedReason || res.resolved.length === 0) return;
                if (!res.optionsIncluded) return;
                appliedRef.current = true;
                let changed = false;
                const next = observations.map((o, i) => {
                    const r = res.resolved[i];
                    if (!r) return o;
                    const merged = { ...o };
                    if (!merged.vendor && r.vendor.value) { merged.vendor = r.vendor.value; changed = true; }
                    if (!merged.itemCategory && r.itemCategory.value) { merged.itemCategory = r.itemCategory.value; changed = true; }
                    if (!merged.currency && r.currency.value) { merged.currency = r.currency.value; changed = true; }
                    return merged;
                });
                if (changed) onChange(next);
            })
            .catch(err => { if (!cancelled) setLoadError(err?.message || 'Could not load the HubSpot value lists'); })
            .finally(() => { if (!cancelled) setIsLoading(false); });
        return () => { cancelled = true; };
        // Deliberately keyed on the customer only: re-running on every keystroke in the section
        // would re-read the schema and fight the rep's own edits.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyCode, wantsOptions]);

    // Known before the server answers: no ERP company means no `dolder_company`, so the section
    // can never be filled. Saying so straight away beats a spinner that resolves into a lock.
    const blockedReason = options?.blockedReason ?? (companyCode ? null : NO_ERP_CUSTOMER_REASON);

    const vendors = options?.enums?.vendor ?? [];
    const categories = options?.enums?.item_category ?? [];
    const currencies = options?.enums?.currency_code ?? [];
    const locked = disabled || !!blockedReason;

    const patch = (index: number, changes: Partial<MarketObservation>) => {
        const next = observations.map((o, i) => (i === index ? { ...o, ...changes } : o));
        onChange(next);
    };

    const addObservation = () => {
        keysRef.current.push(nextRowKey());
        setWantsOptions(true);
        onChange([...observations, { competitorProduct: '' }]);
    };

    const removeObservation = (index: number) => {
        keysRef.current.splice(index, 1);
        setResolutions(prev => prev.filter((_, i) => i !== index));
        onChange(observations.filter((_, i) => i !== index));
    };

    // ── Locked: no ERP customer, or a Dolder company without a HubSpot code ────────────────
    if (blockedReason) {
        return (
            <section className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <Header count={observations.length} />
                <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
                    <Lock className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>{blockedReason}</span>
                </div>
            </section>
        );
    }

    return (
        <section className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <Header count={observations.length} />
                <button
                    type="button"
                    onClick={addObservation}
                    disabled={locked}
                    className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-40"
                >
                    <Plus className="h-3.5 w-3.5" /> Add competitor product
                </button>
            </div>

            {loadError && (
                <div className="mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                    <span>
                        {loadError} — the vendor, category and currency lists come from HubSpot's live
                        schema and cannot be typed by hand. Everything else in this section still saves.
                    </span>
                </div>
            )}

            {observations.length === 0 ? (
                <p className="mt-3 rounded-md border border-dashed border-gray-300 px-3 py-5 text-center text-xs text-gray-400">
                    Nothing recorded. Add what the customer buys elsewhere — product, supplier, volume and
                    price — and it becomes a market data record in HubSpot after the report is filed.
                </p>
            ) : (
                <div className="mt-3 space-y-4">
                    {observations.map((observation, index) => (
                        <ObservationCard
                            key={keysRef.current[index]}
                            index={index}
                            observation={observation}
                            resolution={resolutions[index]}
                            vendors={vendors}
                            categories={categories}
                            currencies={currencies}
                            companyCode={companyCode!}
                            isLoadingOptions={isLoading}
                            disabled={locked}
                            onPatch={changes => patch(index, changes)}
                            onRemove={() => removeObservation(index)}
                        />
                    ))}
                </div>
            )}

            <p className="mt-3 text-[11px] text-gray-400">
                Captured only. Nothing is written to HubSpot from this screen — after the report has
                synced you get a separate step that shows each record before it is created.
            </p>
        </section>
    );
}

function Header({ count }: { count: number }) {
    return (
        <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-800">
                Market data{count > 0 ? ` · ${count}` : ''}
            </h3>
            <p className="text-xs text-gray-500">What the customer buys elsewhere — competitor products.</p>
        </div>
    );
}

// -----------------------------------------------------------------------------
// One observation
// -----------------------------------------------------------------------------

interface CardProps {
    index: number;
    observation: MarketObservation;
    resolution?: Record<'vendor' | 'itemCategory' | 'currency', MarketObservationEnumResolution>;
    vendors: MarketObservationEnumOption[];
    categories: MarketObservationEnumOption[];
    currencies: MarketObservationEnumOption[];
    companyCode: string;
    isLoadingOptions: boolean;
    disabled: boolean;
    onPatch: (changes: Partial<MarketObservation>) => void;
    onRemove: () => void;
}

function ObservationCard({
    index, observation, resolution, vendors, categories, currencies,
    companyCode, isLoadingOptions, disabled, onPatch, onRemove,
}: CardProps) {
    // The picker speaks `AlternativeEntry` (it carries a React key and a subtitle); the draft
    // speaks `MarketObservationAlternative`. Translating in both directions here keeps the
    // stored draft free of UI state and keeps the picker the same component P1 built.
    const entries = useMemo<AlternativeEntry[]>(
        () => (observation.alternatives ?? []).map((a, i) => ({
            key: `obs-${index}-alt-${i}`,
            kind: a.kind,
            productId: a.productId,
            itemCategoryId: a.itemCategoryId,
            displayText: a.displayText,
            companyCode,
        })),
        [observation.alternatives, index, companyCode],
    );

    const handleEntries = (next: AlternativeEntry[]) => {
        // `isPrimary` marks position 0 — the same meaning it has in the Market Intelligence
        // picker and in [Reporting].[MarketdataAlternative]. No second marker beside the order.
        const alternatives: MarketObservationAlternative[] = next.map((e, i) => ({
            kind: e.kind,
            productId: e.productId,
            itemCategoryId: e.itemCategoryId,
            displayText: e.displayText,
            isPrimary: i === 0,
        }));
        onPatch({ alternatives });
    };

    return (
        <div className="rounded-lg border border-gray-200">
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Competitor product {index + 1}
                </span>
                <button
                    type="button"
                    onClick={onRemove}
                    disabled={disabled}
                    className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-40"
                    title="Remove this observation"
                >
                    <Trash2 className="h-4 w-4" />
                </button>
            </div>

            <div className="space-y-3 p-3">
                <label className="flex flex-col gap-1">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                        Product name
                    </span>
                    <input
                        type="text"
                        value={observation.competitorProduct}
                        disabled={disabled}
                        onChange={e => onPatch({ competitorProduct: e.target.value })}
                        placeholder="e.g. Sintal S 69/75 RM"
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-[11px] text-gray-400">Becomes the record's name in HubSpot.</span>
                </label>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <EnumField
                        label="Vendor"
                        options={vendors}
                        value={observation.vendor}
                        suggestion={observation.vendorSuggestion}
                        resolution={resolution?.vendor}
                        isLoading={isLoadingOptions}
                        disabled={disabled}
                        onPick={value => onPatch({ vendor: value ?? undefined })}
                    />
                    <EnumField
                        label="Item category"
                        options={categories}
                        value={observation.itemCategory}
                        suggestion={observation.itemCategorySuggestion}
                        resolution={resolution?.itemCategory}
                        isLoading={isLoadingOptions}
                        disabled={disabled}
                        onPick={value => onPatch({ itemCategory: value ?? undefined })}
                    />
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <label className="flex flex-col gap-1">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                            Volume / year (kg)
                        </span>
                        <input
                            type="number"
                            min={0}
                            value={observation.volumeYearKg ?? ''}
                            disabled={disabled}
                            onChange={e => onPatch({ volumeYearKg: e.target.value === '' ? undefined : Number(e.target.value) })}
                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        />
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                            Price / kg
                        </span>
                        <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={observation.pricePerKg ?? ''}
                            disabled={disabled}
                            onChange={e => onPatch({ pricePerKg: e.target.value === '' ? undefined : Number(e.target.value) })}
                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        />
                    </label>
                    <EnumField
                        label="Currency"
                        options={currencies}
                        value={observation.currency}
                        suggestion={observation.currencySuggestion}
                        resolution={resolution?.currency}
                        isLoading={isLoadingOptions}
                        disabled={disabled}
                        onPick={value => onPatch({ currency: value ?? undefined })}
                    />
                </div>

                <div>
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                        Our alternative
                    </span>
                    <p className="mb-2 text-[11px] text-gray-400">
                        Chosen from our own catalogue — an article or a subclass. The first entry leads
                        the value in HubSpot.
                    </p>
                    <DolderAlternativePicker
                        recordCompanyCode={companyCode}
                        companies={[companyCode]}
                        entries={entries}
                        onChange={handleEntries}
                        disabled={disabled}
                        search={VISIT_REPORT_SEARCH}
                    />
                </div>

                <label className="flex flex-col gap-1">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Note</span>
                    <textarea
                        rows={2}
                        value={observation.note ?? ''}
                        disabled={disabled}
                        onChange={e => onPatch({ note: e.target.value })}
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                </label>
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------------
// One enumeration: a searchable list of what HubSpot allows, plus the AI's proposal
// -----------------------------------------------------------------------------

interface EnumFieldProps {
    label: string;
    options: MarketObservationEnumOption[];
    value: string | undefined;
    suggestion: string | undefined;
    resolution: MarketObservationEnumResolution | undefined;
    isLoading: boolean;
    disabled: boolean;
    onPick: (value: string | null) => void;
}

/**
 * A combobox rather than a `<select>`: 879 vendors and 391 categories are not a dropdown, and
 * the values are opaque codes whose labels are the only thing a rep recognises.
 *
 * What it will not do is accept free text. The list is the live HubSpot schema; anything not in
 * it is refused by HubSpot with a bare 400 naming no field at all, which is exactly the error
 * message nobody can act on.
 */
function EnumField({ label, options, value, suggestion, resolution, isLoading, disabled, onPick }: EnumFieldProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;
        const onDoc = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [isOpen]);

    const selected = options.find(o => o.value === value);
    const filtered = useMemo(() => {
        const needle = search.trim().toLowerCase();
        const base = needle
            ? options.filter(o => o.label.toLowerCase().includes(needle) || o.value.toLowerCase().includes(needle))
            : options;
        // A ceiling on what is rendered, not on what is searchable: 879 DOM nodes in a dropdown
        // inside a wizard step is a visible stutter on a phone, which is where reps use this.
        return base.slice(0, 100);
    }, [options, search]);

    // The suggestion is shown whenever it did not become the value — so the rep can see what
    // was heard and decide, instead of finding an empty field with no explanation.
    const showSuggestion = !!suggestion && (!value || (resolution?.value && resolution.value !== value));
    const candidates = resolution?.candidates ?? [];

    return (
        <div ref={containerRef} className="relative flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">{label}</span>
            <button
                type="button"
                disabled={disabled || (options.length === 0 && !isLoading)}
                onClick={() => setIsOpen(v => !v)}
                className={`flex items-center justify-between gap-1 rounded-lg border px-3 py-2 text-left text-sm disabled:bg-gray-50 disabled:text-gray-400 ${
                    value ? 'border-green-300 bg-white' : 'border-gray-300 bg-white'
                }`}
            >
                <span className={`min-w-0 flex-1 truncate ${value ? 'text-gray-900' : 'text-gray-400'}`}>
                    {selected?.label ?? (value || (isLoading ? 'Loading …' : options.length === 0 ? 'List unavailable' : '— choose —'))}
                </span>
                <span className="flex flex-shrink-0 items-center gap-1">
                    {value && (
                        <span
                            onClick={e => { e.stopPropagation(); onPick(null); }}
                            className="text-gray-400 hover:text-red-600"
                            title="Clear"
                        >
                            <X className="h-3.5 w-3.5" />
                        </span>
                    )}
                    <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                </span>
            </button>

            {showSuggestion && (
                <div className="flex flex-wrap items-center gap-1 text-[11px] text-gray-500">
                    <Sparkles className="h-3 w-3 flex-shrink-0 text-violet-500" />
                    <span>
                        AI heard <span className="font-medium text-gray-700">"{suggestion}"</span>
                        {/* While the lists are still on the way there is no verdict yet, and
                            claiming "not an allowed value" would be a statement the server has
                            not made. Seen on the published app: the wrong sentence flashed for
                            about four seconds before the right one replaced it. */}
                        {isLoading
                            ? ' — checking it against HubSpot …'
                            : resolution?.value
                                ? ' — matched, click to apply:'
                                : candidates.length
                                    ? ' — not an allowed value. Did you mean:'
                                    : ' — not an allowed value; pick one from the list.'}
                    </span>
                    {!isLoading && resolution?.value && (
                        <button
                            type="button"
                            disabled={disabled}
                            onClick={() => onPick(resolution.value)}
                            className="rounded-full border border-violet-200 bg-violet-50 px-1.5 py-0.5 font-medium text-violet-700 hover:bg-violet-100 disabled:opacity-40"
                        >
                            {resolution.label}
                        </button>
                    )}
                    {!isLoading && !resolution?.value && candidates.map(c => (
                        <button
                            key={c.value}
                            type="button"
                            disabled={disabled}
                            onClick={() => onPick(c.value)}
                            className="rounded-full border border-gray-200 bg-white px-1.5 py-0.5 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                        >
                            {c.label}
                        </button>
                    ))}
                </div>
            )}

            {isOpen && (
                <div className="absolute left-0 right-0 top-full z-30 mt-1 rounded-lg border border-gray-200 bg-white shadow-lg">
                    <div className="border-b border-gray-100 p-2">
                        <div className="flex items-center gap-1.5 rounded border border-gray-300 px-2">
                            <Search className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                            <input
                                type="text"
                                autoFocus
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder={`Search ${options.length} values …`}
                                className="w-full py-1.5 text-sm focus:outline-none"
                            />
                        </div>
                    </div>
                    <div className="max-h-56 overflow-y-auto">
                        {filtered.length === 0 && (
                            <div className="px-3 py-3 text-center text-xs text-gray-400">
                                No allowed value matches "{search}".
                            </div>
                        )}
                        {filtered.map(o => (
                            <button
                                key={o.value}
                                type="button"
                                onClick={() => { onPick(o.value); setIsOpen(false); setSearch(''); }}
                                className={`flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-blue-50 ${
                                    o.value === value ? 'bg-blue-50' : ''
                                }`}
                            >
                                {o.value === value && <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-green-600" />}
                                <span className="min-w-0 flex-1 truncate text-sm text-gray-800">{o.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
